/**
 * Visitor token for /api/graphql (electinfo/sites#277).
 *
 * The site's GraphQL proxy needs a short-lived visitor JWT, which nginx issues
 * from GET /api/token into the HttpOnly cookie ei_vt (njs/visitor_token.js).
 * The page cannot read that cookie; it reads ei_vt_exp ("<exp>.<tier>.<ent>",
 * ent = one t/f per data area: voters, individuals, blocks, sites#305) instead,
 * to know when to fetch a new one.
 *
 * build.js injects this module into every bundle, so whichever script runs
 * first wraps window.fetch once. Same-origin /api/graphql calls then get a
 * fresh token first (one refresh shared by concurrent calls) and one retry
 * after a 401; every other request passes through untouched.
 */

const TOKEN_URL = '/api/token';
const REFRESH_MARGIN_SECONDS = 60;

interface TokenHost {
  __electinfoVisitorToken?: {
    refresh: () => Promise<void>;
    tier: () => string | null;
    entitlements: () => string | null;
  };
}

function cookieValue(): string | null {
  const m = /(?:^|;\s*)ei_vt_exp=([^;]+)/.exec(document.cookie);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Seconds until the token expires (negative or null when there is none). */
function secondsLeft(): number | null {
  const value = cookieValue();
  const exp = value ? parseInt(value.split('.')[0], 10) : NaN;
  return Number.isFinite(exp) ? exp - Math.floor(Date.now() / 1000) : null;
}

function isGraphQL(input: RequestInfo | URL): boolean {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin && url.pathname === '/api/graphql';
  } catch {
    return false;
  }
}

function install(): void {
  const host = window as unknown as TokenHost;
  if (host.__electinfoVisitorToken || typeof window.fetch !== 'function') return;
  const original = window.fetch.bind(window);
  let pending: Promise<void> | null = null;

  const refresh = (): Promise<void> => {
    pending ??= original(TOKEN_URL, { credentials: 'same-origin', cache: 'no-store' })
      .then(() => undefined, () => undefined)
      .finally(() => { pending = null; });
    return pending;
  };

  host.__electinfoVisitorToken = {
    refresh,
    tier: () => cookieValue()?.split('.')[1] ?? null,
    // Tokens from before sites#305 have no entitlement key.
    entitlements: () => cookieValue()?.split('.')[2] ?? null,
  };

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!isGraphQL(input)) return original(input, init);
    const left = secondsLeft();
    if (left === null || left < REFRESH_MARGIN_SECONDS) await refresh();
    const res = await original(input, init);
    if (res.status !== 401) return res;
    await refresh();
    return original(input, init);
  };
}

install();

export {};
